/**
 * Template Workflow Action Handlers
 * Implements business logic for template workflow step actions
 */

import { useTemplateWorkflowStore } from '@/stores/templateWorkflowStore';
import api from '@/services/api';
import { router } from '@/router';

/**
 * Helper: Poll agent job until completion
 */
async function pollAgentJobUntilComplete(
  jobId: string,
  onProgress?: (percentage: number) => void
): Promise<any> {
  const maxAttempts = 360; // 360 × 1s = 6 minutes max polling time
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      attempts++;

      // Check if we've exceeded max attempts
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        reject(new Error(`Agent job polling timed out after ${maxAttempts} seconds`));
        return;
      }

      try {
        const response = await api.get(`/agent-jobs/${jobId}`);
        const job = response.data.job;

        // Update progress callback
        if (onProgress && job.progress?.percentage != null) {
          onProgress(job.progress.percentage / 100);
        }

        // Check terminal status
        if (job.status === 'completed') {
          clearInterval(interval);
          resolve(job.result);
        } else if (job.status === 'failed') {
          clearInterval(interval);
          reject(new Error(job.error || 'Job failed'));
        }
      } catch (error) {
        // Only clear interval on the final attempt or terminal errors
        if (attempts >= maxAttempts || (error as any)?.response?.status === 404) {
          clearInterval(interval);
          reject(error);
        } else {
          // Temporary error, log and continue polling
          console.warn(`Polling error (attempt ${attempts}/${maxAttempts}), retrying...`, error);
        }
      }
    }, 1000); // Poll every second
  });
}

/**
 * Helper: Poll validation progress until completion
 */
async function pollValidationProgress(
  conversationId: string,
  onProgress: (progress: any) => void
): Promise<void> {
  const maxAttempts = 600; // 600 × 1s = 10 minutes max polling time
  let attempts = 0;
  let consecutiveErrors = 0;

  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      attempts++;

      // Check if we've exceeded max attempts
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        reject(new Error(`Validation progress polling timed out after ${maxAttempts} seconds`));
        return;
      }

      try {
        const response = await api.get(
          `/conversations/${conversationId}/template/validation/progress`
        );

        // Reset consecutive error counter on success
        consecutiveErrors = 0;

        if (response.data.success && response.data.inProgress) {
          // Update progress callback with current progress
          onProgress(response.data.progress);
        } else if (!response.data.inProgress) {
          // Validation completed (progress cleared by backend)
          clearInterval(interval);
          resolve();
        }
      } catch (error) {
        // If 404, validation is complete (progress cleared)
        if ((error as any)?.response?.status === 404) {
          clearInterval(interval);
          resolve();
          return;
        }

        // Track consecutive errors
        consecutiveErrors++;

        // Only fail after multiple consecutive errors (transient network issues)
        if (consecutiveErrors >= 5) {
          clearInterval(interval);
          reject(new Error('Validation progress polling failed after multiple errors'));
        } else {
          console.warn(
            `Polling error (attempt ${attempts}/${maxAttempts}, consecutive: ${consecutiveErrors}), retrying...`,
            error
          );
        }
      }
    }, 1000); // Poll every second
  });
}

/**
 * Start Validation and GCS Upload - Step 1 of template workflow
 * Starts validation and polls for progress updates
 */
export async function startValidation(conversationId: string): Promise<void> {
  console.log('🔍 Starting template validation and GCS upload for:', conversationId);

  const store = useTemplateWorkflowStore.getState();

  try {
    // Update status to in_progress
    store.updateValidationStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
    });

    // Start validation (this will begin the async process on backend)
    // Don't await this - start polling immediately
    const validationPromise = api.post(
      `/conversations/${conversationId}/template/validate-and-upload`
    );

    // Start polling for progress updates
    // This will update the store as progress changes
    const pollingPromise = pollValidationProgress(conversationId, (progress) => {
      store.updateValidationStatus(conversationId, {
        progress: progress.percentage,
      });

      console.log(
        `📊 Validation progress: ${progress.percentage}% - ${progress.currentStep}${
          progress.currentAsset ? ` (${progress.currentAsset})` : ''
        }`
      );
    });

    // Wait for both validation and polling to complete
    const [response] = await Promise.all([validationPromise, pollingPromise]);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to validate and upload template');
    }

    const { validation, upload } = response.data;

    console.log('✅ Validation and upload completed:', { validation, upload });

    // Update store with final results
    store.updateValidationStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      validationPath: 'validation.json',
      layoutCount: validation.validSlides,
      totalAssets: validation.totalAssets,
      assetsByType: {
        images: upload.uploadedAssets || 0,
      },
      recommendations: `Validated ${validation.validSlides} slides and uploaded ${upload.uploadedAssets} unique assets to GCS.`,
      // GCS upload info
      gcsUploadComplete: true,
      uploadedSlides: upload.successfulSlides,
      uploadedAssets: upload.uploadedAssets,
      totalUniqueAssets: upload.totalUniqueAssets,
    });

    // Enable layout generation step
    store.updateLayoutGenerationStatus(conversationId, {
      canProceed: true,
      totalLayouts: validation.validSlides,
    });

    // Progress to next step
    store.progressToNextTemplateStep(conversationId);

  } catch (error) {
    console.error('❌ Failed to complete validation and upload:', error);

    // Extract detailed error message if available
    let errorMessage = 'Validation and upload failed';
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    if ((error as any)?.response?.data?.error) {
      errorMessage = (error as any).response.data.error;
    }

    store.updateValidationStatus(conversationId, {
      status: 'error',
      error: errorMessage,
    });
    throw error;
  }
}

/**
 * Start Layout Generation - Step 2 of template workflow
 */
export async function startLayoutGeneration(conversationId: string): Promise<void> {
  console.log('🎨 Starting layout generation for:', conversationId);

  const store = useTemplateWorkflowStore.getState();

  try {
    // Update status to in_progress
    store.updateLayoutGenerationStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
      isExpanded: true,
    });

    // Call backend API to start layout generation
    const response = await api.post(
      `/conversations/${conversationId}/template/layout-generation`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to start layout generation');
    }

    const jobId = response.data.jobId;
    console.log('✅ Layout generation job started:', jobId);

    // Store job ID
    store.updateLayoutGenerationStatus(conversationId, {
      jobId,
    });

    // Poll until completion
    const result = await pollAgentJobUntilComplete(jobId, (percentage) => {
      store.updateLayoutGenerationStatus(conversationId, {
        progress: Math.round(percentage * 100),
      });
    });

    console.log('✅ Layout generation completed:', result);

    // Update store with completion
    store.updateLayoutGenerationStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      layouts: result.layouts || [],
      totalLayouts: result.totalLayouts,
      completedLayouts: result.totalLayouts,
    });

    // Enable design document step
    store.updateDesignDocumentStatus(conversationId, {
      canProceed: true,
    });

    // Progress to next step
    store.progressToNextTemplateStep(conversationId);

  } catch (error) {
    console.error('❌ Failed to complete layout generation:', error);
    store.updateLayoutGenerationStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Layout generation failed',
    });
    throw error;
  }
}

/**
 * Start Design Document Generation - Step 3 of template workflow
 */
export async function startDesignDocument(conversationId: string): Promise<void> {
  console.log('📝 Starting design document generation for:', conversationId);

  const store = useTemplateWorkflowStore.getState();

  try {
    // Update status to in_progress
    store.updateDesignDocumentStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
      isExpanded: true,
    });

    // Call backend API to start design document generation
    const response = await api.post(
      `/conversations/${conversationId}/template/design-document`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to start design document generation');
    }

    const jobId = response.data.jobId;
    console.log('✅ Design document job started:', jobId);

    // Store job ID
    store.updateDesignDocumentStatus(conversationId, {
      jobId,
    });

    // Poll until completion
    const result = await pollAgentJobUntilComplete(jobId, (percentage) => {
      store.updateDesignDocumentStatus(conversationId, {
        progress: Math.round(percentage * 100),
      });
    });

    console.log('✅ Design document generation completed:', result);

    // Update store with completion
    store.updateDesignDocumentStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      documentPath: result.documentPath,
      sections: result.sections || [],
      wordCount: result.wordCount,
      createdAt: new Date(),
    });

    console.log('🎉 Template workflow completed!');

  } catch (error) {
    console.error('❌ Failed to complete design document generation:', error);
    store.updateDesignDocumentStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Design document generation failed',
    });
    throw error;
  }
}

/**
 * View Validation Results
 */
export async function viewValidation(conversationId: string): Promise<void> {
  console.log('👁️ Viewing validation results for:', conversationId);

  // Navigate to gallery to show validation results
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'settings' },
  });
}

/**
 * View Generated Layouts
 */
export async function viewLayouts(conversationId: string): Promise<void> {
  console.log('👁️ Viewing generated layouts for:', conversationId);

  // Navigate to gallery to show layouts
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'scenes' },
  });
}

/**
 * View Design Document
 */
export async function viewDesignDocument(conversationId: string): Promise<void> {
  console.log('👁️ Viewing design document for:', conversationId);

  // Navigate to gallery to show design document
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'settings' },
  });
}

/**
 * Reset Template Workflow
 */
export async function resetTemplateWorkflow(conversationId: string): Promise<void> {
  console.log('🔄 Resetting template workflow for:', conversationId);

  const store = useTemplateWorkflowStore.getState();

  try {
    // Call backend API to reset workflow
    const response = await api.post(
      `/conversations/${conversationId}/template/reset`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to reset template workflow');
    }

    // Reset local store
    store.resetTemplateWorkflow(conversationId);

    console.log('✅ Template workflow reset successfully');

  } catch (error) {
    console.error('❌ Failed to reset template workflow:', error);
    throw error;
  }
}

/**
 * Rerun Validation - Reset only the validation step
 */
export async function rerunValidation(conversationId: string): Promise<void> {
  console.log('🔄 Resetting validation step for:', conversationId);

  const store = useTemplateWorkflowStore.getState();

  try {
    // Call backend API to reset validation
    const response = await api.post(
      `/conversations/${conversationId}/template/validation/reset`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to reset validation');
    }

    console.log('✅ Validation reset successfully');

    // Reset the entire workflow to initial state
    // This will set currentStep back to 'validation' and reset all steps
    store.resetTemplateWorkflow(conversationId);

  } catch (error) {
    console.error('❌ Failed to reset validation:', error);
    throw error;
  }
}
